
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [bioRef, bioInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [timelineRef, timelineInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [achievementsRef, achievementsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const timelineEvents = [
    {
      year: '2023',
      title: 'Fetih Partisi Genel Başkanlığı',
      description: 'Türkiye\'nin güçlü, müreffeh ve bağımsız bir geleceği için Fetih Partisi Genel Başkanlığı görevini üstlendi. Milletin refahını merkeze alan siyasi vizyon ile harekete geçti.'
    },
    {
      year: '2022',
      title: 'ULAK Genel Başkanlığı',
      description: 'Yerli ve milli iletişim teknolojilerinin geliştirilmesi amacıyla ULAK\'ın Genel Başkanlığı görevine getirildi. Türkiye\'nin teknolojik bağımsızlığı için kritik adımlar atıldı.'
    },
    {
      year: '2020',
      title: 'Uluslararası İş Dünyası Faaliyetleri',
      description: 'Milli kalkınmaya katkı sağlayan yatırım ve girişimcilik faaliyetlerini uluslararası arenaya taşıdı. Türk iş insanlarının önünü açacak stratejik ortaklıklar kurdu.'
    },
    {
      year: '2018',
      title: 'Sektörel Liderlik & Yönetim',
      description: 'Farklı sektörlerde yönetim kurulu üyelikleri ve liderlik görevleri üstlenerek iş dünyasındaki etki alanını genişletti. Stratejik büyüme projelerini hayata geçirdi.'
    },
    {
      year: '2015',
      title: 'Girişimcilik Yolculuğu',
      description: 'Türkiye\'nin ekonomik kalkınmasına katkı sağlamak amacıyla girişimcilik faaliyetlerine başladı. İstihdam yaratan ve katma değer üreten projeler geliştirdi.'
    },
    {
      year: '2010',
      title: 'Siyasi Kariyer Başlangıcı',
      description: 'Türkiye\'nin geleceğine dair derin bir sorumluluk duygusuyla siyasi alanda aktif rol almaya başladı. Toplumsal sorunlara çözüm üretme misyonunu benimsedi.'
    }
  ];

  const values = [
    {
      icon: 'ri-flag-2-line',
      title: 'Millete Hizmet',
      description: 'Her karar ve eylemde milletin çıkarlarını ön planda tutmak. Vatandaşın refahı ve mutluluğu için çalışmak en temel ilkedir.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Milli Bağımsızlık',
      description: 'Türkiye\'nin siyasi, ekonomik ve teknolojik bağımsızlığını korumak ve güçlendirmek. Yerli ve milli değerlere sahip çıkmak.'
    },
    {
      icon: 'ri-rocket-2-line',
      title: 'Kalkınma & Üretim',
      description: 'Türkiye\'nin üretim kapasitesini artırmak, teknoloji ve inovasyonda öncü olmak. Milli kalkınmayı her alanda desteklemek.'
    }
  ];

  const achievements = [
    { number: 'Fetih Partisi', label: 'Genel Başkanı' },
    { number: 'ULAK', label: 'Genel Başkanı' },
    { number: '10+', label: 'Yıllık Siyasi Deneyim' },
    { number: 'Ulusal', label: 'Tanınırlık & Etki' },
  ];

  return (
    <div className="min-h-screen bg-cream-light pb-16 lg:pb-0">
      <Header />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial={{ opacity: 0 }}
        animate={heroInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="relative pt-32 pb-20 bg-gradient-to-b from-navy to-navy-light"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-xs text-gold uppercase tracking-widest mb-4 block"
          >
            Fetih Partisi Genel Başkanı · ULAK Genel Başkanı · İş İnsanı
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-serif text-5xl lg:text-7xl font-bold text-white mb-6"
          >
            Fatih Sinan Yılmaz
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="font-sans text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Siyaset, teknoloji ve iş dünyasında Türkiye'nin güçlü geleceği için mücadele eden lider
          </motion.p>
        </div>
      </motion.section>

      {/* Achievements Bar */}
      <motion.section
        ref={achievementsRef}
        initial={{ opacity: 0, y: 30 }}
        animate={achievementsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="bg-gold py-10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {achievements.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                animate={achievementsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <p className="font-serif text-2xl lg:text-3xl font-bold text-navy">{item.number}</p>
                <p className="font-sans text-sm text-navy/70 mt-1">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Biography Section */}
      <motion.section
        ref={bioRef}
        initial={{ opacity: 0, y: 50 }}
        animate={bioInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="py-20 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/1e9a79a9d54d29e64c65859d7d500968.jpg"
                alt="Fatih Sinan Yılmaz"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>

            <div>
              <span className="font-sans text-sm text-gray-500 mb-4 block uppercase tracking-widest">/ Biyografi</span>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-navy leading-tight mb-8">
                Millet İçin <span className="text-gold">Kararlı Duruş</span>
              </h2>
              
              <div className="space-y-6">
                <p className="font-sans text-lg text-gray-700 leading-relaxed">
                  Fatih Sinan Yılmaz; Fetih Partisi Genel Başkanı, ULAK Genel Başkanı ve iş insanı kimliğiyle Türkiye'nin siyasi, teknolojik ve ekonomik kalkınması için aktif rol üstlenen bir liderdir.
                </p>
                
                <p className="font-sans text-lg text-gray-700 leading-relaxed">
                  Fetih Partisi Genel Başkanı olarak Türkiye'nin güçlü, müreffeh ve bağımsız bir geleceği için siyasi mücadelesini sürdürmektedir. Milletin refahını, sosyal adaleti ve ulusal bağımsızlığı temel ilkeler olarak benimseyen Yılmaz, siyaseti bir hizmet aracı olarak görmektedir.
                </p>

                <p className="font-sans text-lg text-gray-700 leading-relaxed">
                  ULAK Genel Başkanlığı göreviyle yerli ve milli iletişim teknolojilerinin geliştirilmesine öncülük etmekte, Türkiye'nin teknolojik bağımsızlığını pekiştirmek için stratejik adımlar atmaktadır. İş dünyasındaki girişimcilik faaliyetleriyle de milli kalkınmaya somut katkılar sağlamaktadır.
                </p>

                <p className="font-sans text-lg font-bold text-navy italic">
                  "Türkiye'nin gücü, milletinin birliğinden ve kararlılığından kaynaklanır."
                </p>
                <p className="font-sans text-base text-gray-500">— Fatih Sinan Yılmaz</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Timeline Section */}
      <motion.section
        ref={timelineRef}
        initial={{ opacity: 0, y: 50 }}
        animate={timelineInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="bg-white py-20 lg:py-32"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-navy mb-4">
              Kariyer Yolculuğu
            </h2>
            <p className="font-sans text-lg text-gray-600">
              Önemli dönüm noktaları ve görevler
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-200 hidden lg:block"></div>

            <div className="space-y-12">
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={event.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={timelineInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex-col lg:gap-8`}
                >
                  <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className="bg-white border border-gray-200 rounded-xl p-7 shadow-md hover:shadow-lg transition-shadow duration-300">
                      <span className="font-sans text-base font-bold text-gold mb-2 block">
                        {event.year}
                      </span>
                      <h3 className="font-serif text-xl font-bold text-navy mb-3">
                        {event.title}
                      </h3>
                      <p className="font-sans text-base text-gray-600 leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>

                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gold rounded-full border-4 border-white shadow-md"></div>

                  <div className="hidden lg:block w-5/12"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Values Section */}
      <motion.section
        ref={valuesRef}
        initial={{ opacity: 0, y: 50 }}
        animate={valuesInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="py-20 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-navy mb-4">
              Değerler ve İlkeler
            </h2>
            <p className="font-sans text-lg text-gray-600">
              Siyasi ve iş hayatını şekillendiren temel değerler
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-cream rounded-2xl p-10 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-5xl text-gold`}></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-navy mb-4">
                  {value.title}
                </h3>
                <p className="font-sans text-base text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default AboutPage;
